import type { TimeOfDay } from './buildTimeList';
import { timeOfDayMinutes } from './timeOfDayMinutes';

const MINUTES_IN_DAY = 24 * 60;

function ceilToStep(minutes: number, step: number): number {
    return Math.ceil(minutes / step) * step;
}

function floorToStep(minutes: number, step: number): number {
    return Math.floor(minutes / step) * step;
}

function minutesToTimeOfDay(minutes: number): TimeOfDay {
    return { hour: Math.floor(minutes / 60), minute: minutes % 60 };
}

/**
 * Clamp `time` to `[minTime, maxTime]`, snapping to the nearest `step`-aligned
 * option (as generated by `buildTimeList`) rather than to the raw bound.
 * Inputs below the effective `minTime` snap up to it. Inputs above the
 * effective `maxTime` snap down to it. Others are returned unchanged. When no
 * `step`-aligned option exists inside `[minTime, maxTime]`, `time` is returned
 * unchanged since there is nothing to snap to. Only the time-of-day of
 * `minTime`/`maxTime` is used.
 */
export function snapTimeToBounds(time: TimeOfDay, minTime?: Date, maxTime?: Date, step = 30): TimeOfDay {
    const totalMinutes = time.hour * 60 + time.minute;

    const effectiveMin = minTime ? ceilToStep(timeOfDayMinutes(minTime), step) : undefined;
    const effectiveMax = maxTime ? floorToStep(timeOfDayMinutes(maxTime), step) : undefined;

    const noOptionInBounds =
        effectiveMin !== undefined &&
        (effectiveMin >= MINUTES_IN_DAY || (effectiveMax !== undefined && effectiveMin > effectiveMax));
    if (noOptionInBounds) {
        return time;
    }

    if (effectiveMin !== undefined && totalMinutes < effectiveMin) {
        return minutesToTimeOfDay(effectiveMin);
    }
    if (effectiveMax !== undefined && totalMinutes > effectiveMax) {
        return minutesToTimeOfDay(effectiveMax);
    }
    return time;
}
